import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FiBookOpen,
  FiVideo,
  FiPlay,
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiCalendar,
  FiClock,
  FiAward,
  FiTarget,
  FiActivity,
  FiPlus,
  FiArrowRight,
  FiCheckCircle,
  FiAlertCircle,
  FiBarChart2,
  FiStar,
  FiZap,
} from 'react-icons/fi';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { dashboardAPI } from '../services/api';

const badgeClass = (v) => ({ success: 'bg-emerald-100 text-emerald-800', warning: 'bg-amber-100 text-amber-800', danger: 'bg-red-100 text-red-800', info: 'bg-slate-100 text-slate-700' }[v] || 'bg-slate-100 text-slate-700');

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeFilter, setTimeFilter] = useState('week');
  
  const { user, isLoading: authLoading } = useAuth();
  const { showError } = useNotification();
  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚀 Dashboard: Starting loadDashboardData...', { timeFilter, userRole: user?.role });
      }
      setLoading(true);
      setError(null);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📡 Dashboard: Making API call to dashboardAPI.getDashboardData...');
      }
      const response = await dashboardAPI.getDashboardData({ timeFilter, role: user?.role });
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Dashboard: API response received:', response);
      }
      
      const data = response.data?.data || response.data || response;
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Dashboard: Processed data:', data);
      }
      setDashboardData(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Dashboard API Error:', err);
        console.error('❌ Dashboard API Error Details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          url: err.config?.url
        });
      }
      setError('Failed to load dashboard data. Please try again later.');
      showError('Failed to load dashboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [timeFilter, user, showError]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🎯 Dashboard useEffect:', { 
        user: user ? { id: user.id, role: user.role, name: user.firstName } : null, 
        authLoading, 
        timeFilter,
        hasUser: !!user,
        condition: user && !authLoading
      });
    }
    if (user && !authLoading) {
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Dashboard: Loading dashboard data for user:', user.role);
        console.log('🔄 Dashboard: Calling loadDashboardData...');
      }
      loadDashboardData();
    } else if (process.env.NODE_ENV === 'development') {
      console.log('⏳ Dashboard: Waiting for auth...', { 
        hasUser: !!user, 
        authLoading,
        reason: !user ? 'No user' : authLoading ? 'Auth loading' : 'Unknown'
      });
    }
  }, [timeFilter, user, authLoading, loadDashboardData]);
  

  
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };
  
  const getQuickActions = () => {
    const actions = {
      student: [
        {
          title: 'Practice Tests',
          description: 'Take practice tests',
          icon: FiBookOpen,
          path: '/practice-tests',
          color: 'blue'
        },
        {
          title: 'Join Session',
          description: 'Join a live learning session',
          icon: FiVideo,
          path: '/sessions',
          color: 'green'
        },
        {
          title: 'Play Games',
          description: 'Learn through educational games',
          icon: FiPlay,
          path: '/games',
          color: 'purple'
        },
        {
          title: 'View Progress',
          description: 'Check your learning progress',
          icon: FiTrendingUp,
          path: '/analytics/progress',
          color: 'orange'
        }
      ],
      teacher: [
        {
          title: 'Exam Settings',
          description: 'Configure exams and tests',
          icon: FiPlus,
          path: '/exam-settings',
          color: 'blue'
        },
        {
          title: 'Meetings',
          description: 'Start or join meetings',
          icon: FiVideo,
          path: '/meetings',
          color: 'green'
        },
        {
          title: 'View Students',
          description: 'Manage your students',
          icon: FiUsers,
          path: '/students',
          color: 'purple'
        },
        {
          title: 'Analytics',
          description: 'View performance analytics',
          icon: FiTrendingUp,
          path: '/analytics',
          color: 'orange'
        }
      ],
      parent: [
        {
          title: 'Children Progress',
          description: 'View your children\'s progress',
          icon: FiTrendingUp,
          path: '/children/progress',
          color: 'blue'
        },
        {
          title: 'Test Results',
          description: 'Check recent test results',
          icon: FiBookOpen,
          path: '/children/results',
          color: 'green'
        },
        {
          title: 'Meetings',
          description: 'View meetings and sessions',
          icon: FiCalendar,
          path: '/meetings',
          color: 'purple'
        },
        {
          title: 'Messages',
          description: 'Communicate with teachers',
          icon: FiUsers,
          path: '/messages',
          color: 'orange'
        }
      ],
      admin: [
        {
          title: 'User Management',
          description: 'Manage system users',
          icon: FiUsers,
          path: '/admin/users',
          color: 'blue'
        },
        {
          title: 'Content Management',
          description: 'Manage tests and content',
          icon: FiBookOpen,
          path: '/admin/content',
          color: 'green'
        },
        {
          title: 'System Analytics',
          description: 'View system performance',
          icon: FiActivity,
          path: '/admin/analytics',
          color: 'purple'
        },
        {
          title: 'Settings',
          description: 'Configure system settings',
          icon: FiTarget,
          path: '/admin/settings',
          color: 'orange'
        }
      ]
    };
    
    return actions[user?.role] || actions.student;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-sm text-slate-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData && !loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center max-w-sm px-4">
          <FiAlertCircle className="text-slate-400 mb-4 mx-auto" size={40} />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Unable to load dashboard</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button type="button" onClick={loadDashboardData} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800">Try again</button>
        </div>
      </div>
    );
  }
  
  const quickActions = getQuickActions();

  // Chart color schemes
  const chartColors = {
    primary: '#3B82F6',
    secondary: '#10B981',
    accent: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6',
    pink: '#EC4899'
  };

  const pieColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  // Format chart data
  const perfData = dashboardData?.performanceHistory || dashboardData?.chartData?.performanceOverTime || [];
  const formatPerformanceData = () => {
    if (!Array.isArray(perfData) || perfData.length === 0) return [];
    return perfData.map(item => ({
      ...item,
      date: item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : (item.label || ''),
      score: item.score ?? item.value
    }));
  };

  const subjectData = dashboardData?.subjectPerformance || dashboardData?.chartData?.subjectPerformance || {};
  const formatSubjectData = () => {
    if (typeof subjectData !== 'object' || Object.keys(subjectData).length === 0) return [];
    return Object.entries(subjectData).map(([subject, score]) => ({
      subject: subject.charAt(0).toUpperCase() + subject.slice(1),
      score: parseFloat(score) || 0,
      fill: pieColors[Object.keys(subjectData).indexOf(subject) % pieColors.length]
    }));
  };

  const recommendationsList = dashboardData?.recommendations || dashboardData?.aiRecommendations || [];

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-6 text-sm text-amber-800">
            <FiAlertCircle size={16} className="shrink-0" />
            <span>Some data may not be up to date.</span>
            <button type="button" onClick={loadDashboardData} className="ml-auto font-medium underline hover:no-underline">Refresh</button>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              {getGreeting()}, {dashboardData?.user?.name || user?.firstName || user?.name || 'Student'}!
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {user?.role === 'student' && 'Ready to continue your learning journey?'}
              {user?.role === 'teacher' && 'Manage your students and meetings.'}
              {user?.role === 'parent' && 'Track your children\'s progress.'}
              {user?.role === 'admin' && 'Overview of your platform.'}
              {!['student', 'teacher', 'parent', 'admin'].includes(user?.role) && 'Welcome to your dashboard.'}
            </p>
            {dashboardData?.user?.streak > 0 && (
              <span className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-md bg-amber-100 text-amber-800 text-xs font-medium">
                <FiAward className="h-4 w-4" />{dashboardData.user.streak} day streak
              </span>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-2">
            <label className="sr-only">Time period</label>
            <select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)} className="text-sm text-slate-700 bg-transparent border-0 py-1 pr-6 focus:ring-0">
              <option value="day">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            {user?.role === 'student' && (
              <>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiBookOpen size={20} /></div>
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-slate-900">{dashboardData?.stats?.testsCompleted ?? 0}</div>
                  <div className="text-xs text-slate-500">Tests Completed</div>
                  <div className="text-xs text-slate-500 mt-0.5">{dashboardData?.trends?.testsCompleted > 0 ? <span className="text-emerald-600 inline-flex items-center gap-0.5"><FiTrendingUp size={10} />+{dashboardData.trends.testsCompleted}%</span> : 'No change'}</div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600"><FiTarget size={20} /></div>
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-slate-900">{dashboardData?.stats?.averageScore ?? 0}%</div>
                  <div className="text-xs text-slate-500">Average Score</div>
                  <div className="text-xs text-slate-500 mt-0.5">{dashboardData?.trends?.averageScore > 0 ? <span className="text-emerald-600 inline-flex items-center gap-0.5"><FiTrendingUp size={10} />+{dashboardData.trends.averageScore}%</span> : 'Stable'}</div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600"><FiClock size={20} /></div>
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-slate-900">{dashboardData?.stats?.examHours ?? 0}h</div>
                  <div className="text-xs text-slate-500">Exam Hours</div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600"><FiAward size={20} /></div>
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-slate-900">#{dashboardData?.stats?.rank ?? '—'}</div>
                  <div className="text-xs text-slate-500">Rank</div>
                  <div className="text-xs text-slate-500 mt-0.5">{dashboardData?.peerComparison?.rankChange > 0 ? <span className="text-emerald-600"><FiTrendingUp size={10} className="inline" /> Up {dashboardData.peerComparison.rankChange}</span> : dashboardData?.peerComparison?.rankChange < 0 ? <span className="text-red-600"><FiTrendingDown size={10} className="inline" /> {Math.abs(dashboardData.peerComparison.rankChange)}</span> : 'No change'}</div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600"><FiZap size={20} /></div>
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-slate-900">{dashboardData?.stats?.gamePoints ?? 0}</div>
                  <div className="text-xs text-slate-500">Game Points</div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiAward size={20} /></div>
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-slate-900">{dashboardData?.stats?.achievements ?? 0}</div>
                  <div className="text-xs text-slate-500">Achievements</div>
                </div>
              </div>
            </div>
              </>
            )}

            {user?.role === 'teacher' && dashboardData?.teacherStats && (
              <>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers size={20} /></div>
                <div><div className="text-lg font-semibold text-slate-900">{dashboardData.teacherStats.assignedStudents ?? 0}</div><div className="text-xs text-slate-500">Assigned Students</div></div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600"><FiVideo size={20} /></div>
                <div><div className="text-lg font-semibold text-slate-900">{dashboardData.teacherStats.upcomingMeetings ?? 0}</div><div className="text-xs text-slate-500">Upcoming Meetings</div></div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600"><FiCalendar size={20} /></div>
                <div><div className="text-lg font-semibold text-slate-900">{dashboardData.teacherStats.upcomingSessions ?? 0}</div><div className="text-xs text-slate-500">Upcoming Sessions</div></div>
              </div>
            </div>
              </>
            )}

            {user?.role === 'parent' && dashboardData?.children?.length > 0 && (
              dashboardData.children.map((child) => (
            <div key={child.id} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers size={20} /></div>
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-slate-900 truncate">{child.name}</div>
                  <div className="text-xs text-slate-500">{child.grade} · {child.totalTests} tests</div>
                  <div className="text-xs text-slate-500 mt-0.5">Avg: {child.averageScore}%{child.recentScore != null ? ` · Last: ${child.recentScore}%` : ''}</div>
                </div>
              </div>
            </div>
              ))
            )}

            {user?.role === 'admin' && dashboardData?.userStats && (
              <>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers size={20} /></div>
                <div><div className="text-lg font-semibold text-slate-900">{(dashboardData.userStats.student ?? 0) + (dashboardData.userStats.teacher ?? 0) + (dashboardData.userStats.parent ?? 0)}</div><div className="text-xs text-slate-500">Total Users</div></div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600"><FiBookOpen size={20} /></div>
                <div><div className="text-lg font-semibold text-slate-900">{((dashboardData.questionStats?.pending ?? 0) + (dashboardData.questionStats?.approved ?? 0) + (dashboardData.questionStats?.rejected ?? 0)) || 0}</div><div className="text-xs text-slate-500">Questions</div></div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600"><FiBarChart2 size={20} /></div>
                <div><div className="text-lg font-semibold text-slate-900">{dashboardData.testStats?.totalTests ?? 0}</div><div className="text-xs text-slate-500">Tests</div></div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600"><FiVideo size={20} /></div>
                <div><div className="text-lg font-semibold text-slate-900">{dashboardData.sessionStats?.totalSessions ?? 0}</div><div className="text-xs text-slate-500">Sessions</div></div>
              </div>
            </div>
              </>
            )}

            {user?.role === 'teacher' && !dashboardData?.teacherStats && (
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers size={20} /></div>
                  <div><div className="text-lg font-semibold text-slate-900">—</div><div className="text-xs text-slate-500">Assigned Students</div></div>
                </div>
              </div>
            )}
            {user?.role === 'parent' && (!dashboardData?.children || dashboardData.children.length === 0) && (
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers size={20} /></div>
                  <div><div className="text-lg font-semibold text-slate-900">0</div><div className="text-xs text-slate-500">Children linked</div><div className="text-xs text-slate-500 mt-0.5">Link in Profile</div></div>
                </div>
              </div>
            )}
          </div>

        {user?.role === 'student' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-900">Performance trend</h3>
                <FiTrendingUp className="h-5 w-5 text-slate-400" />
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={formatPerformanceData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }} />
                    <Line type="monotone" dataKey="score" stroke={chartColors.primary} strokeWidth={2} dot={{ fill: chartColors.primary, r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-900">Subject performance</h3>
                <FiBarChart2 className="h-5 w-5 text-slate-400" />
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={formatSubjectData()} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="score">
                      {formatSubjectData().map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value}%`, 'Score']} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-900">Peer comparison</h3>
                <FiUsers className="h-5 w-5 text-slate-400" />
              </div>
              {dashboardData?.peerComparison ? (
                <div className="flex flex-wrap items-center gap-4 py-4">
                  <div><div className="text-xs text-slate-500">Your average</div><div className="text-xl font-semibold text-slate-900">{dashboardData.peerComparison.yourAverage}%</div></div>
                  <span className="text-slate-400 font-medium">vs</span>
                  <div><div className="text-xs text-slate-500">Peer average</div><div className="text-xl font-semibold text-slate-700">{dashboardData.peerComparison.peerAverage}%</div></div>
                  <p className="w-full text-sm text-slate-600 flex items-center gap-1.5 mt-2">
                    <FiStar className="h-4 w-4 text-amber-500" />
                    You're {dashboardData.peerComparison.yourAverage > dashboardData.peerComparison.peerAverage ? 'above' : 'below'} average
                  </p>
                </div>
              ) : (
                <div className="py-8 text-center text-slate-500">
                  <FiUsers size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No peer data available</p>
                </div>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-slate-900">Recommendations</h3>
                <FiStar className="h-5 w-5 text-slate-400" />
              </div>
              {recommendationsList.length > 0 ? (
                <ul className="space-y-3">
                  {recommendationsList.slice(0, 3).map((rec, index) => (
                    <li key={index} className="flex gap-3">
                      <FiStar className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-slate-900">{typeof rec === 'string' ? rec : (rec.title || rec.topic)}</div>
                        {(typeof rec === 'object' && rec.description) && <div className="text-xs text-slate-500 mt-0.5">{rec.description}</div>}
                        {typeof rec === 'object' && rec.priority && (
                          <span className={`inline-flex mt-1 px-2 py-0.5 rounded text-xs font-medium ${badgeClass(rec.priority === 'high' ? 'danger' : rec.priority === 'medium' ? 'warning' : 'info')}`}>{rec.priority} priority</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="py-8 text-center text-slate-500">
                  <FiStar size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recommendations yet</p>
                  <p className="text-xs mt-1">Complete more tests for insights</p>
                </div>
              )}
            </div>
        </div>
        )}

        <div className="mb-6">
          <h2 className="text-sm font-medium text-slate-900 mb-3">Quick actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Link key={index} to={action.path} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex items-center gap-3 hover:border-slate-300 transition-colors">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><Icon size={20} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900">{action.title}</div>
                    <div className="text-xs text-slate-500 truncate">{action.description}</div>
                  </div>
                  <FiArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
                </Link>
              );
            })}
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {user?.role === 'admin' && dashboardData?.recentActivity && (
            <>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-3">
              <h3 className="text-sm font-medium text-slate-900">Recent users</h3>
              <Link to="/admin/users" className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">View all <FiArrowRight size={14} /></Link>
            </div>
            <div className="space-y-2">
              {(dashboardData.recentActivity.users || []).slice(0, 5).map((u) => (
                <div key={u._id} className="flex items-center gap-3 py-2">
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers size={14} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</div>
                    <div className="text-xs text-slate-500">{u.role}{u.createdAt ? ` · ${new Date(u.createdAt).toLocaleDateString()}` : ''}</div>
                  </div>
                </div>
              ))}
              {(dashboardData.recentActivity.users || []).length === 0 && <p className="text-sm text-slate-500 py-4">No users yet</p>}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-3">
              <h3 className="text-sm font-medium text-slate-900">Recent questions</h3>
              <Link to="/admin/questions" className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">View all <FiArrowRight size={14} /></Link>
            </div>
            <div className="space-y-2">
              {(dashboardData.recentActivity.questions || []).slice(0, 5).map((q) => (
                <div key={q._id} className="flex items-center gap-3 py-2">
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"><FiBookOpen size={14} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900 truncate">{(q.questionText || q.question || '').slice(0, 50)}…</div>
                    <div className="text-xs text-slate-500">{q.subject} · {q.status}</div>
                  </div>
                </div>
              ))}
              {(dashboardData.recentActivity.questions || []).length === 0 && <p className="text-sm text-slate-500 py-4">No questions yet</p>}
            </div>
          </div>
            </>
          )}
          {user?.role !== 'admin' && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-3">
              <h3 className="text-sm font-medium text-slate-900">Recent activity</h3>
              <Link to="/analytics/activity" className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">View all <FiArrowRight size={14} /></Link>
            </div>
            <div className="space-y-2">
              {dashboardData?.recentActivity && Array.isArray(dashboardData.recentActivity) && dashboardData.recentActivity.length > 0 ? (
                dashboardData.recentActivity.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-center gap-3 py-2">
                    <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                      {activity.type === 'test' && <FiBookOpen size={14} />}
                      {activity.type === 'session' && <FiVideo size={14} />}
                      {activity.type === 'game' && <FiPlay size={14} />}
                      {activity.type === 'grading' && <FiCheckCircle size={14} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900">{activity.title}</div>
                      <div className="text-xs text-slate-500">
                        {activity.score && `Score: ${activity.score}% `}
                        {activity.attendees && `${activity.attendees} attendees `}
                        {activity.count && `${activity.count} items `}
                        {activity.time}
                      </div>
                    </div>
                    {activity.status && (
                      <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${activity.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>{activity.status}</span>
                    )}
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-slate-500">
                  <FiActivity size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent activity</p>
                  <p className="text-xs mt-1">Start tests or sessions to see activity here.</p>
                </div>
              )}
            </div>
          </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-3">
              <h3 className="text-sm font-medium text-slate-900">Notifications</h3>
              <Link to="/notifications" className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">View all <FiArrowRight size={14} /></Link>
            </div>
            <div className="space-y-3">
              {dashboardData?.notifications && dashboardData.notifications.length > 0 ? (
                dashboardData.notifications.slice(0, 3).map((notification) => (
                  <div key={notification.id} className={`py-2 pr-2 border-l-2 pl-3 ${notification.unread ? 'border-slate-900 bg-slate-50/50' : 'border-transparent'}`}>
                    <div className="text-sm font-medium text-slate-900">{notification.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{notification.message}</div>
                    <div className="text-xs text-slate-400 mt-1">{notification.time}</div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-slate-500">
                  <FiAlertCircle size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No notifications</p>
                  <p className="text-xs mt-1">You're all caught up.</p>
                </div>
              )}
            </div>
          </div>

          {user?.role === 'teacher' && dashboardData?.pendingTasks && dashboardData.pendingTasks.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 lg:col-span-2">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-3">
                <h3 className="text-sm font-medium text-slate-900">Pending tasks</h3>
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium">{dashboardData.pendingTasks.length}</span>
              </div>
              <div className="space-y-2">
                {dashboardData.pendingTasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{task.title}</div>
                      <div className="text-xs text-slate-500">{task.count && `${task.count} items · `}Due: {task.dueDate}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${task.priority === 'high' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{task.priority}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {user?.role === 'parent' && dashboardData?.children && dashboardData.children.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 lg:col-span-2">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-3">
                <h3 className="text-sm font-medium text-slate-900">Children</h3>
                <Link to="/parent/children" className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">View details <FiArrowRight size={14} /></Link>
              </div>
              <div className="space-y-4">
                {dashboardData.children.map((child) => (
                  <div key={child.id} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50/50">
                    <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-medium text-sm shrink-0 overflow-hidden">
                      {child.avatar ? <img src={child.avatar} alt={child.name} className="h-full w-full object-cover" /> : child.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900">{child.name}</div>
                      <div className="text-xs text-slate-500">{child.grade} · Recent: {child.recentScore}% · Rank #{child.rank}</div>
                      <div className="flex gap-3 mt-1 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><FiBookOpen size={12} />{child.testsThisWeek} tests</span>
                        <span className="inline-flex items-center gap-1"><FiClock size={12} />{child.examHours}h exam</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;